import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

/* ============================================
   ANIMATIONS
   ============================================ */

const glitchAnim = keyframes`
  0% { transform: translate(0); filter: none; }
  10% { transform: translate(-5px, 3px); filter: hue-rotate(90deg); }
  20% { transform: translate(3px, -2px); filter: hue-rotate(180deg) saturate(2); }
  30% { transform: translate(-3px, 5px); filter: hue-rotate(270deg); }
  40% { transform: translate(5px, -3px); filter: none; }
  50% { transform: translate(-2px, 2px); filter: hue-rotate(90deg) brightness(1.5); }
  60% { transform: translate(0); filter: none; }
  100% { transform: translate(0); filter: none; }
`;

const scanline = keyframes`
  0% { top: -10%; }
  100% { top: 110%; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/* ============================================
   STYLED COMPONENTS
   ============================================ */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: #000;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 10vh 12vw;
  font-family: 'JetBrains Mono', 'Consolas', 'Courier New', monospace;
  overflow: hidden;
  animation: ${fadeIn} 0.3s ease;
`;

const Scanline = styled.div`
  position: absolute;
  top: -10%;
  left: 0;
  width: 100%;
  height: 4px;
  background: rgba(34, 211, 238, 0.08);
  box-shadow: 0 0 20px rgba(34, 211, 238, 0.15);
  animation: ${scanline} 3s linear infinite;
  pointer-events: none;
`;

const CRTOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    0deg,
    rgba(0, 0, 0, 0.15) 0px,
    rgba(0, 0, 0, 0.15) 1px,
    transparent 1px,
    transparent 3px
  );
  pointer-events: none;
  z-index: 1;
`;

const BootLine = styled.div`
  font-size: ${props => props.fontSize || '0.9rem'};
  color: ${props => props.color || '#22d3ee'};
  margin-bottom: ${props => props.mb || '6px'};
  opacity: 0;
  animation: ${fadeIn} 0.15s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  white-space: pre;
  position: relative;
  z-index: 2;
  text-shadow: 0 0 8px ${props => props.glow || 'rgba(34, 211, 238, 0.3)'};

  @media (max-width: 768px) {
    font-size: ${props => props.mobileFontSize || '0.7rem'};
  }
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 16px;
  border: 1px solid #22d3ee;
  border-radius: 2px;
  overflow: hidden;
  margin: 4px 0;
  opacity: 0;
  animation: ${fadeIn} 0.15s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    width: 200px;
    height: 12px;
  }
`;

const fillBar = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #22d3ee, #10b981);
  animation: ${fillBar} 1.2s ease forwards;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 10px rgba(34, 211, 238, 0.5);
`;

const Cursor = styled.span`
  display: inline-block;
  width: 8px;
  height: 16px;
  background: #22d3ee;
  animation: ${blink} 1s step-end infinite;
  margin-left: 4px;
  vertical-align: middle;
`;

const GlitchWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: #000;
  z-index: 9999;
  pointer-events: none;
  animation: ${glitchAnim} 0.4s ease forwards;
`;

/* ============================================
   BOOT SEQUENCE DATA
   ============================================ */

const bootLines = [
  { text: '', mb: '10px', delay: 0 },
  { text: 'DEV_KANSARA OS v2.5', fontSize: '1.4rem', mobileFontSize: '1rem', color: '#e2e8f0', mb: '4px', delay: 0.3, glow: 'rgba(226, 232, 240, 0.3)' },
  { text: '═══════════════════════════════════════', color: '#334155', mb: '16px', delay: 0.5 },
  { text: 'Running POST............. OK', delay: 0.8, color: '#10b981', glow: 'rgba(16, 185, 129, 0.3)' },
  { text: '', delay: 1.0 },
  { text: 'CPU:  Full-Stack Engineer @ XOPS', delay: 1.2, color: '#94a3b8', glow: 'rgba(148, 163, 184, 0.2)' },
  { text: 'RAM:  Go | React | Python | AWS | Docker', delay: 1.4, color: '#94a3b8', glow: 'rgba(148, 163, 184, 0.2)' },
  { text: 'DISK: USC MS CS \'25', delay: 1.6, color: '#94a3b8', glow: 'rgba(148, 163, 184, 0.2)' },
  { text: '', delay: 1.8 },
  { text: 'Loading modules...', delay: 2.0 },
  { type: 'progress', delay: 2.2, fillDelay: 2.4 },
  { text: '', delay: 3.6 },
  { text: 'Mounting /home/dev/portfolio...', delay: 3.8, color: '#10b981', glow: 'rgba(16, 185, 129, 0.3)' },
  { text: '', delay: 4.2 },
  { text: '> System ready.', delay: 4.5, color: '#22d3ee', fontSize: '1.1rem', mobileFontSize: '0.85rem', mb: '0' },
];

const TOTAL_DURATION = 5200; // ms before portfolio comes back

/* ============================================
   COMPONENT
   ============================================ */

const BootSequence = ({ onComplete }) => {
  const [phase, setPhase] = useState('glitch'); // 'glitch' | 'boot' | 'done'

  const handleComplete = useCallback(() => {
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    // Lock body scroll on mobile to prevent content showing behind
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Phase 1: Glitch for 400ms
    const glitchTimer = setTimeout(() => {
      setPhase('boot');
    }, 400);

    // Phase 2: Boot sequence runs, then complete
    const completeTimer = setTimeout(() => {
      setPhase('done');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      setTimeout(handleComplete, 300);
    }, 400 + TOTAL_DURATION);

    return () => {
      clearTimeout(glitchTimer);
      clearTimeout(completeTimer);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [handleComplete]);

  if (phase === 'glitch') {
    return <GlitchWrapper />;
  }

  if (phase === 'done') {
    return null;
  }

  return (
    <Overlay>
      <CRTOverlay />
      <Scanline />
      {bootLines.map((line, i) => {
        if (line.type === 'progress') {
          return (
            <ProgressBarContainer key={i} delay={`${line.delay}s`}>
              <ProgressFill delay={`${line.fillDelay}s`} />
            </ProgressBarContainer>
          );
        }
        return (
          <BootLine
            key={i}
            fontSize={line.fontSize}
            mobileFontSize={line.mobileFontSize}
            color={line.color}
            mb={line.mb}
            delay={`${line.delay}s`}
            glow={line.glow}
          >
            {line.text}
            {i === bootLines.length - 1 && <Cursor />}
          </BootLine>
        );
      })}
    </Overlay>
  );
};

export default BootSequence;
